import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, CardHeader, CardContent, CardFooter } from '../components/ui';
import { useAuthStore } from '../store/auth';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

const legalAcceptanceSchema = z.object({
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the Terms of Service',
  }),
  privacyAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the Privacy Policy',
  }),
});

type LegalAcceptanceFormData = z.infer<typeof legalAcceptanceSchema>;

export default function LegalAcceptance() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LegalAcceptanceFormData>({
    resolver: zodResolver(legalAcceptanceSchema),
    defaultValues: {
      termsAccepted: false,
      privacyAccepted: false,
    },
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('You must be logged in to accept terms');
        return;
      }

      // Get ID token for API request
      const idToken = await currentUser.getIdToken();

      // Update user with legal acceptance via API
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: LEGAL_VERSIONS.TERMS,
          privacyAcceptedAt: new Date().toISOString(),
          privacyVersion: LEGAL_VERSIONS.PRIVACY,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update legal acceptance');
      }

      // Refresh user data
      await refreshUser();

      // Redirect to the intended destination or dashboard
      const returnUrl = location.state?.returnUrl || '/dashboard';
      navigate(returnUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to accept terms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-tennis-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Card className="shadow-xl">
          <CardHeader>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white text-center">
              Accept Terms and Privacy Policy
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-center mt-2">
              Please review and accept our Terms of Service and Privacy Policy to continue
            </p>
          </CardHeader>

          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent className="space-y-6">
              {/* Legal Acceptance Checkboxes */}
              <div className="space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    {...register('termsAccepted')}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      I agree to the{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                      >
                        Terms of Service
                      </Link>
                    </span>
                    {errors.termsAccepted && (
                      <p className="text-red-600 dark:text-red-400 text-xs mt-1">
                        {errors.termsAccepted.message}
                      </p>
                    )}
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    {...register('privacyAccepted')}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      I agree to the{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                      >
                        Privacy Policy
                      </Link>
                    </span>
                    {errors.privacyAccepted && (
                      <p className="text-red-600 dark:text-red-400 text-xs mt-1">
                        {errors.privacyAccepted.message}
                      </p>
                    )}
                  </div>
                </label>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full btn-primary"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Accepting...' : 'Accept and Continue'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
